import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import PrimaryButton from '@components/PrimaryButton';
import LoadingAnimation from '@components/LoadingAnimation';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useToast } from '@hooks/useToast';
import { useIntermediaryAnalyticsQuery } from '@features/intermediary-analytics/queries';
import type { BankSortBy, IntermediaryPaymentType } from '@models/intermediaryAnalytics';
import { Breakpoints } from '@utils/constants';

import s from './intermediary-analytics-page.module.css';
import { useIntermediaryAnalyticsFilters } from '../../features/intermediary-analytics/useIntermediaryAnalyticsFilters';
import {
  buildChartOption,
  getBankNameFromChartClick,
} from '@features/intermediary-analytics/intermediary-analytics-chart';

const IntermediaryAnalyticsPage = () => {
  const toast = useToast();
  const isLargeScreen = useMediaQuery({ query: `(min-width: ${Breakpoints.xxxl})` });
  const filters = useIntermediaryAnalyticsFilters();
  const { data, isLoading } = useIntermediaryAnalyticsQuery(filters.submittedFilters, {
    enabled: filters.areFiltersReadyForQuery,
  });
  const { option: chartOption, chartHeight } = useMemo(
    () =>
      buildChartOption(
        data ?? { data: {} },
        filters.sortBy,
        filters.processorConfig,
        isLargeScreen,
        filters.filterByBrand,
      ),
    [data, filters.sortBy, filters.processorConfig, isLargeScreen, filters.filterByBrand],
  );
  const hasChartData = Boolean(data?.data && Object.keys(data.data).length > 0);

  const handleChartClick = async (params: Parameters<typeof getBankNameFromChartClick>[0]) => {
    const bankName = getBankNameFromChartClick(params);
    if (!bankName) return;

    try {
      await navigator.clipboard.writeText(bankName);
      toast.success(`Copied "${bankName}"`);
    } catch {
      toast.error('Could not copy bank name.');
    }
  };

  const handleSubmit = () => {
    if (filters.hasDateError) {
      toast.error(
        `Please check the date range filters. Max range is ${filters.maxDateRange} days.`,
      );
      return;
    }
    filters.handleSubmit();
  };

  const isPageLoading = !filters.areFiltersReadyForQuery || isLoading;
  if (isPageLoading) return <LoadingAnimation />;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className={s.container} data-content-width="wide">
        <div className={s.header}>
          <Stack direction="row" alignItems="center" gap={3}>
            <Typography className={s.title} variant="h5">
              Intermediary Analytics
            </Typography>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="sort-by-label">Sort by</InputLabel>
              <Select
                labelId="sort-by-label"
                value={filters.sortBy}
                label="Sort by"
                onChange={(e) => filters.setSortBy(e.target.value as BankSortBy)}>
                <MenuItem value="alphabetical">A-Z</MenuItem>
                <MenuItem value="totalPaymentCount">Total payment count</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="sort-by-label">Filter by Brand</InputLabel>
              <Select
                labelId="filter-by-brand-label"
                value={filters.filterByBrand}
                label="Filter by Brand"
                onChange={(e) => filters.setFilterByBrand(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                {filters.brandCodes.map((brandCode) => (
                  <MenuItem value={brandCode}>{brandCode}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            <DatePicker
              label="Start date"
              value={filters.startDate}
              maxDate={filters.today}
              onChange={(date) => date && filters.setStartDate(date)}
              slotProps={{ textField: { size: 'small' as const, sx: { width: 180 } } }}
            />
            <DatePicker
              label="End date"
              minDate={filters.startDate}
              maxDate={filters.endDateMax}
              value={filters.endDate}
              onChange={(date) => date && filters.setEndDate(date)}
              slotProps={{ textField: { size: 'small' as const, sx: { width: 180 } } }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="payment-type-label">Payment type</InputLabel>
              <Select
                labelId="payment-type-label"
                value={filters.paymentType}
                label="Payment type"
                onChange={(e) => filters.setPaymentType(e.target.value as IntermediaryPaymentType)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="subscription">Subscription</MenuItem>
                <MenuItem value="unscheduled">Unscheduled</MenuItem>
              </Select>
            </FormControl>
            <PrimaryButton title="Submit" isDisabled={!filters.isDirty} onClick={handleSubmit} />
          </Stack>
        </div>

        {hasChartData ? (
          <>
            <div className={s.chartWrapper}>
              <ReactECharts
                option={chartOption}
                style={{ height: chartHeight, minHeight: 500 }}
                opts={{ renderer: 'canvas' }}
                onEvents={{ click: handleChartClick }}
                notMerge
              />
            </div>
            <div className={s.legendRow}>
              <div className={s.legendItem}>
                <span className={s.legendLabel}>Bubble size:</span>
                <span>Payment count</span>
              </div>
              <div className={s.legendItem}>
                <span className={s.legendLabel}>Bubble color:</span>
                <span>Success rate % (green = high, red = low, yellow = medium)</span>
              </div>
              <div className={s.legendItem}>
                <span className={s.legendLabel}>Best issuer:</span>
                <span>✦ BEST label + gold glow</span>
              </div>
            </div>
          </>
        ) : (
          <div className={s.emptyState}>
            <h2 className={s.emptyStateTitle}>No analytics data available</h2>
            <p className={s.emptyStateSubtitle}>Try changing filters and submit again.</p>
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default IntermediaryAnalyticsPage;
